// The upload card's color, as one pure decision.
//
// Green means "parsed by the reader this file claims to be for". When a
// specialist (26AS / GL ledger / Tally) REFUSED the file and the generic
// tabular parser took it instead, the totals on show are raw statement
// arithmetic — possibly counting subtotal rows — and the card must say so
// (fleet finding #5: a mis-totalled 26AS showed ~2x TDS under green).
import type { ParseStats } from './types'

export type UploadToneColor = 'green' | 'yellow' | 'red'

/** The generic kinds — the ones a specialist refusal demotes to yellow. */
const GENERIC_KINDS = ['statement', 'ledger']

/** What the refused specialist was, in the reader's words. */
const PARSER_LABEL: Record<string, string> = {
  traces_26as: 'a 26AS',
  pdf_26as: 'a 26AS PDF',
  gl_ledger: 'a GL ledger export',
  tally_ledger: 'a Tally ledger',
}

export function uploadTone(
  status: string,
  stats?: Pick<ParseStats, 'kind' | 'probe_failures'> | null,
): UploadToneColor {
  if (status === 'parsed') {
    const generic = GENERIC_KINDS.includes(stats?.kind ?? '')
    if (generic && (stats?.probe_failures?.length ?? 0) > 0) return 'yellow'
    return 'green'
  }
  if (status === 'needs_mapping') return 'yellow'
  return 'red'
}

/** The warning sentence for a parsed-but-yellow card, or null. */
export function probeFailureSentence(
  stats?: Pick<ParseStats, 'kind' | 'probe_failures'> | null,
): string | null {
  if (!GENERIC_KINDS.includes(stats?.kind ?? '')) return null
  const first = stats?.probe_failures?.[0]
  if (!first) return null
  const idx = first.indexOf(': ')
  const parser = idx > 0 ? first.slice(0, idx) : ''
  const reason = idx > 0 ? first.slice(idx + 2) : first
  const looked = PARSER_LABEL[parser] ?? 'a known format'
  return `This looked like ${looked} but failed its own check: ${reason} — showing raw statement totals instead.`
}
